/**
 * Iter 126 — Onboarding Kits + Questionnaires admin page.
 *
 * Kept intentionally lean: three tabs (Kits · Questionnaires · Responses).
 * Each tab lets an admin CRUD the entity and shows enough context to be
 * useful on day one. Advanced editors (question-field builder, kit
 * task-template builder) live inside their own dialogs to avoid crowding
 * the list surface.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, Send, Plus, Trash2, FileText, Package, Users, Copy as CopyIcon, ExternalLink, Layers, QrCode, Bookmark, Share2, Download as DownloadIcon, ListPlus, ArrowUp, ArrowDown, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";

const FIELD_KINDS = ["short_text", "long_text", "single_choice", "multi_choice", "number", "date", "rating", "file", "url", "email"];

export default function OnboardingKits() {
  const [tab, setTab] = useState("kits");

  return (
    <div className="space-y-6 px-3 sm:px-6" data-testid="onboarding-kits-page">
      <PageHeader
        breadcrumb="Client success"
        title="Onboarding kits"
        description="Smart bundles that turn first-touch into a repeatable choreography — questionnaires, tasks, SOPs and a welcome message across every channel your client uses."
      />
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="rounded-none h-10 flex w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="kits" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="ok-tab-kits">
            <Package className="w-4 h-4 mr-1.5" /> Kits
          </TabsTrigger>
          <TabsTrigger value="questionnaires" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="ok-tab-questionnaires">
            <FileText className="w-4 h-4 mr-1.5" /> Questionnaires
          </TabsTrigger>
          <TabsTrigger value="responses" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="ok-tab-responses">
            <Users className="w-4 h-4 mr-1.5" /> Responses
          </TabsTrigger>
        </TabsList>
        <TabsContent value="kits" className="mt-4"><KitsTab /></TabsContent>
        <TabsContent value="questionnaires" className="mt-4"><QuestionnairesTab /></TabsContent>
        <TabsContent value="responses" className="mt-4"><ResponsesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────── Questionnaires ───────────────────────

function QuestionnairesTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try { const { data } = await api.get(`/workspaces/${activeWorkspaceId}/questionnaires`); setRows(data || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Couldn't load")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Reusable, typed intake forms. Send via public link OR embed in a kit.</p>
        <Button className="rounded-none" onClick={() => setEditing("new")} data-testid="questionnaire-new-btn"><Plus className="w-4 h-4 mr-1" /> New questionnaire</Button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground py-6">Loading…</div>
        : rows.length === 0 ? <EmptyState title="No questionnaires yet" description="Author your first typed intake form." icon={FileText} testid="questionnaires-empty" />
          : (
          <ul className="divide-y divide-border border border-border" data-testid="questionnaire-list">
            {rows.map((q) => (
              <li key={q.id} className="p-3 hover:bg-secondary/20 flex items-center gap-3" data-testid={`questionnaire-row-${q.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{q.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{(q.fields || []).length} fields · AI summary {q.summarise_with_ai ? "on" : "off"}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEditing(q.id)} data-testid={`questionnaire-edit-${q.id}`}>Edit</Button>
              </li>
            ))}
          </ul>
        )}
      {editing !== null && (
        <QuestionnaireEditor
          questionnaireId={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function QuestionnaireEditor({ questionnaireId, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState([]);
  const [aiOn, setAiOn] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!questionnaireId) return;
    api.get(`/questionnaires/${questionnaireId}`).then(({ data }) => {
      setName(data.name); setDescription(data.description || "");
      setFields(data.fields || []); setAiOn(!!data.summarise_with_ai);
    });
  }, [questionnaireId]);

  const addField = () => setFields((prev) => [...prev, { key: `f${prev.length + 1}`, label: "New field", kind: "short_text", required: false }]);
  const updateField = (i, patch) => setFields((prev) => prev.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const removeField = (i) => setFields((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), description, fields, summarise_with_ai: aiOn };
      if (questionnaireId) {
        await api.patch(`/questionnaires/${questionnaireId}`, payload);
      } else {
        await api.post(`/workspaces/${activeWorkspaceId}/questionnaires`, payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-3xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="questionnaire-editor">
        <DialogHeader>
          <DialogTitle>{questionnaireId ? "Edit questionnaire" : "New questionnaire"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px]">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-none" data-testid="q-name" />
          </div>
          <div>
            <Label className="label-eyebrow text-[10px]">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="rounded-none" data-testid="q-desc" />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={aiOn} onChange={(e) => setAiOn(e.target.checked)} data-testid="q-ai" />
            <Sparkles className="w-3.5 h-3.5" /> Add AI executive summary + red-flag detection to every submission
          </label>
          <div className="border border-border">
            <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border">
              <div className="text-xs font-semibold uppercase tracking-widest">Fields ({fields.length})</div>
              <Button size="sm" variant="outline" className="rounded-none" onClick={addField} data-testid="q-add-field"><Plus className="w-3.5 h-3.5 mr-1" /> Add field</Button>
            </div>
            <ul className="divide-y divide-border">
              {fields.map((f, i) => (
                <li key={i} className="p-2 grid grid-cols-6 sm:grid-cols-12 gap-2 items-start" data-testid={`q-field-${i}`}>
                  <Input value={f.key} onChange={(e) => updateField(i, { key: e.target.value })} placeholder="key" className="col-span-3 sm:col-span-2 h-8 rounded-none text-xs font-mono" />
                  <Input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} placeholder="Label" className="col-span-3 sm:col-span-4 h-8 rounded-none text-xs" />
                  <select value={f.kind} onChange={(e) => updateField(i, { kind: e.target.value })} className="col-span-3 sm:col-span-3 h-8 rounded-none border border-border text-xs bg-background px-1">
                    {FIELD_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <label className="col-span-2 sm:col-span-2 flex items-center gap-1 text-[10px]">
                    <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} /> req
                  </label>
                  <Button size="sm" variant="ghost" className="col-span-1 h-8 rounded-none px-1" onClick={() => removeField(i)} data-testid={`q-field-del-${i}`}><Trash2 className="w-3 h-3" /></Button>
                  {(f.kind === "single_choice" || f.kind === "multi_choice") && (
                    <Input value={(f.options || []).join(", ")} onChange={(e) => updateField(i, { options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} placeholder="option1, option2, option3" className="col-span-6 sm:col-span-12 h-8 rounded-none text-xs" />
                  )}
                </li>
              ))}
              {fields.length === 0 && <li className="p-4 text-[11px] text-muted-foreground italic">No fields yet.</li>}
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="q-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────── Kits ───────────────────────

function KitsTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [sops, setSops] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [wsTemplates, setWsTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [launching, setLaunching] = useState(null); // kit object
  const [showLibrary, setShowLibrary] = useState(false);
  const [saveTplFor, setSaveTplFor] = useState(null); // kit obj → open save-as-template dialog
  const [shareTpl, setShareTpl] = useState(null);      // template obj → mint share token
  const [importTokenOpen, setImportTokenOpen] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const [k, q, s, t, wt] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/onboarding-kits`),
        api.get(`/workspaces/${activeWorkspaceId}/questionnaires`),
        api.get(`/workspaces/${activeWorkspaceId}/sops`),
        api.get(`/onboarding-kits/templates/library`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/kit-templates`).catch(() => ({ data: [] })),
      ]);
      setRows(k.data || []); setQuestionnaires(q.data || []);
      setSops((s.data || []).slice(0, 200));
      setTemplates(t.data || []);
      setWsTemplates(wt.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const cloneTemplate = async (slug) => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/onboarding-kits/from-template?slug=${slug}`);
      toast.success("Kit cloned from starter library");
      setShowLibrary(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Clone failed")); }
  };

  const cloneWsTemplate = async (tplId) => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/onboarding-kits/from-workspace-template/${tplId}`);
      toast.success("Kit cloned from workspace template");
      setShowLibrary(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Clone failed")); }
  };

  const deleteWsTemplate = async (tplId) => {
    if (!window.confirm("Remove this workspace template? Existing kits cloned from it stay untouched.")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/kit-templates/${tplId}`);
      toast.success("Template removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">A one-click sequence — welcome message, questionnaire, tasks and SOPs — for every recurring client onboarding.</p>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-none" onClick={() => setImportTokenOpen(true)} data-testid="kit-import-token-btn"><DownloadIcon className="w-4 h-4 mr-1" /> Import via share link</Button>
          <Button variant="outline" className="rounded-none" onClick={() => setShowLibrary(true)} data-testid="kit-library-btn"><Layers className="w-4 h-4 mr-1" /> Template library</Button>
          <Button className="rounded-none" onClick={() => setEditing("new")} data-testid="kit-new-btn"><Plus className="w-4 h-4 mr-1" /> New kit</Button>
        </div>
      </div>
      {loading ? <div className="text-sm text-muted-foreground py-6">Loading…</div>
        : rows.length === 0 ? <EmptyState title="No kits yet" description="Bundle a welcome message, questionnaire, tasks and SOPs into one clickable sequence." icon={Package} testid="kits-empty" />
          : (
          <ul className="divide-y divide-border border border-border" data-testid="kit-list">
            {rows.map((k) => (
              <li key={k.id} className="p-3 hover:bg-secondary/20 flex items-center gap-3" data-testid={`kit-row-${k.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {k.name}
                    {k.cloned_from_template && <span className="text-[10px] text-muted-foreground"> · from {k.cloned_from_template}</span>}
                    {k.cloned_from_workspace_template && <span className="text-[10px] text-muted-foreground"> · from workspace template</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {(k.task_templates || []).length} tasks · {(k.sop_ids || []).length} SOPs · channels: {(k.channels || []).join(", ")}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setLaunching(k)} data-testid={`kit-launch-${k.id}`}><Send className="w-3.5 h-3.5 mr-1" /> Launch</Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setSaveTplFor(k)} title="Save as reusable workspace template" data-testid={`kit-save-tpl-${k.id}`}><Bookmark className="w-3.5 h-3.5 mr-1" /> Save as template</Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEditing(k.id)} data-testid={`kit-edit-${k.id}`}>Edit</Button>
              </li>
            ))}
          </ul>
        )}
      {showLibrary && (
        <Dialog open onOpenChange={() => setShowLibrary(false)}>
          <DialogContent className="w-[95vw] max-w-3xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="kit-library-dialog">
            <DialogHeader><DialogTitle>Kit template library</DialogTitle></DialogHeader>
            {wsTemplates.length > 0 && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-primary mt-2 mb-1">Your workspace templates</div>
                <p className="text-xs text-muted-foreground -mt-0.5 mb-2">Private snapshots saved by your admins.</p>
                <ul className="divide-y divide-border border border-border" data-testid="ws-kit-templates-list">
                  {wsTemplates.map((t) => (
                    <li key={t.id} className="p-4 hover:bg-secondary/20 flex items-start gap-3" data-testid={`ws-kit-template-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-2"><Bookmark className="w-3.5 h-3.5 text-primary" />{t.name}</div>
                        {t.description && <div className="text-[11px] text-muted-foreground mb-1">{t.description}</div>}
                        <div className="text-[10px] text-muted-foreground">
                          Saved by {t.created_by_name || "an admin"} · {(t.created_at || "").slice(0, 10)}
                          {" · "}{(t.task_templates || []).length} tasks
                          {t.questionnaire_template && ` · questionnaire: ${t.questionnaire_template.name}`}
                          {typeof t.clone_count === "number" && ` · cloned ${t.clone_count}×`}
                        </div>
                      </div>
                      <Button size="sm" className="rounded-none" onClick={() => cloneWsTemplate(t.id)} data-testid={`ws-kit-clone-${t.id}`}>Clone</Button>
                      <Button size="sm" variant="outline" className="rounded-none" onClick={() => setShareTpl(t)} title="Share with another workspace" data-testid={`ws-kit-share-${t.id}`}><Share2 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => deleteWsTemplate(t.id)} data-testid={`ws-kit-tpl-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </li>
                  ))}
                </ul>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-4 mb-1">Starter library</div>
              </>
            )}
            <p className="text-sm text-muted-foreground">Curated templates. One click clones the kit AND its questionnaire into your workspace — edit anything after.</p>
            <ul className="divide-y divide-border border border-border">
              {templates.map((t) => (
                <li key={t.slug} className="p-4 hover:bg-secondary/20 flex items-start gap-3" data-testid={`kit-template-${t.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-[11px] text-muted-foreground mb-1">{t.description}</div>
                    <div className="text-[10px] text-muted-foreground">{(t.task_templates || []).length} tasks · questionnaire: {t.questionnaire_template?.name || "—"}</div>
                  </div>
                  <Button size="sm" className="rounded-none" onClick={() => cloneTemplate(t.slug)} data-testid={`kit-clone-${t.slug}`}>Clone</Button>
                </li>
              ))}
            </ul>
          </DialogContent>
        </Dialog>
      )}
      {saveTplFor && (
        <SaveAsTemplateDialog
          kit={saveTplFor}
          wsId={activeWorkspaceId}
          onClose={() => setSaveTplFor(null)}
          onSaved={() => { setSaveTplFor(null); load(); }}
        />
      )}
      {shareTpl && (
        <ShareTemplateDialog
          template={shareTpl}
          wsId={activeWorkspaceId}
          onClose={() => setShareTpl(null)}
        />
      )}
      {importTokenOpen && (
        <ImportViaTokenDialog
          wsId={activeWorkspaceId}
          onClose={() => setImportTokenOpen(false)}
          onImported={() => { setImportTokenOpen(false); load(); }}
        />
      )}
      {editing !== null && (
        <KitEditor
          kitId={editing === "new" ? null : editing}
          questionnaires={questionnaires}
          sops={sops}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {launching && (
        <KitLauncher
          kit={launching}
          onClose={() => setLaunching(null)}
          onLaunched={() => setLaunching(null)}
        />
      )}
    </div>
  );
}

function KitEditor({ kitId, questionnaires, sops, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const [form, setForm] = useState({
    name: "", description: "", welcome_subject: "", welcome_body_html: "",
    questionnaire_id: "", task_templates: [], sop_ids: [], channels: ["email"],
    kickoff_agenda_html: "",
  });
  const [busy, setBusy] = useState(false);
  // Feb 2026 — Bulk-add tasks dialog toggle.
  const [bulkOpen, setBulkOpen] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!kitId) return;
    api.get(`/onboarding-kits/${kitId}`).then(({ data }) => setForm({
      name: data.name || "", description: data.description || "",
      welcome_subject: data.welcome_subject || "",
      welcome_body_html: data.welcome_body_html || "",
      questionnaire_id: data.questionnaire_id || "",
      task_templates: data.task_templates || [],
      sop_ids: data.sop_ids || [],
      channels: data.channels || ["email"],
      kickoff_agenda_html: data.kickoff_agenda_html || "",
    }));
  }, [kitId]);

  const toggleChannel = (c) => set("channels", form.channels.includes(c) ? form.channels.filter((x) => x !== c) : [...form.channels, c]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, questionnaire_id: form.questionnaire_id || null };
      if (kitId) await api.patch(`/onboarding-kits/${kitId}`, payload);
      else await api.post(`/workspaces/${activeWorkspaceId}/onboarding-kits`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-3xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="kit-editor">
        <DialogHeader><DialogTitle>{kitId ? "Edit kit" : "New kit"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-eyebrow text-[10px]">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-9 rounded-none" data-testid="kit-name" /></div>
          <div><Label className="label-eyebrow text-[10px]">Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="rounded-none" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow text-[10px]">Welcome subject</Label>
              <Input value={form.welcome_subject} onChange={(e) => set("welcome_subject", e.target.value)} className="h-9 rounded-none" /></div>
            <div><Label className="label-eyebrow text-[10px]">Questionnaire</Label>
              <select value={form.questionnaire_id} onChange={(e) => set("questionnaire_id", e.target.value)} className="h-9 rounded-none w-full border border-border bg-background px-2 text-sm" data-testid="kit-questionnaire">
                <option value="">— None —</option>
                {questionnaires.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select></div>
          </div>
          <div><Label className="label-eyebrow text-[10px]">Welcome body (HTML)</Label>
            <Textarea value={form.welcome_body_html} onChange={(e) => set("welcome_body_html", e.target.value)} rows={4} className="rounded-none font-mono text-xs" /></div>
          <div>
            <Label className="label-eyebrow text-[10px]">Delivery channels</Label>
            <div className="flex gap-2 mt-1">
              {["email", "whatsapp", "portal"].map((c) => (
                <button key={c} type="button" onClick={() => toggleChannel(c)}
                  className={`px-3 py-1.5 text-xs border ${form.channels.includes(c) ? "border-primary bg-primary/10" : "border-border"}`}
                  data-testid={`kit-channel-${c}`}
                >{c}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="label-eyebrow text-[10px]">Task templates (title • owner_role • due_offset_days)</Label>
            <ul className="border border-border divide-y divide-border" data-testid="kit-task-list">
              {form.task_templates.map((t, i) => (
                <li key={i} className="grid grid-cols-6 sm:grid-cols-12 gap-1.5 p-1.5 items-center" data-testid={`kit-task-row-${i}`}>
                  {/* Feb 2026 — Reorder arrows within the flat list. */}
                  <div className="col-span-1 flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => {
                        if (i === 0) return;
                        const next = form.task_templates.slice();
                        [next[i - 1], next[i]] = [next[i], next[i - 1]];
                        set("task_templates", next);
                      }}
                      disabled={i === 0}
                      className={`w-4 h-3 flex items-center justify-center ${i === 0 ? "text-muted-foreground/30 cursor-not-allowed" : "text-muted-foreground hover:text-primary"}`}
                      title="Move up"
                      data-testid={`kit-task-up-${i}`}
                    ><ArrowUp className="w-3 h-3" /></button>
                    <button
                      type="button"
                      onClick={() => {
                        if (i === form.task_templates.length - 1) return;
                        const next = form.task_templates.slice();
                        [next[i], next[i + 1]] = [next[i + 1], next[i]];
                        set("task_templates", next);
                      }}
                      disabled={i === form.task_templates.length - 1}
                      className={`w-4 h-3 flex items-center justify-center ${i === form.task_templates.length - 1 ? "text-muted-foreground/30 cursor-not-allowed" : "text-muted-foreground hover:text-primary"}`}
                      title="Move down"
                      data-testid={`kit-task-down-${i}`}
                    ><ArrowDown className="w-3 h-3" /></button>
                  </div>
                  <Input value={t.title} onChange={(e) => set("task_templates", form.task_templates.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} placeholder="Task title" className="col-span-5 sm:col-span-5 h-8 rounded-none text-xs" />
                  <Input value={t.owner_role || ""} onChange={(e) => set("task_templates", form.task_templates.map((x, idx) => idx === i ? { ...x, owner_role: e.target.value } : x))} placeholder="owner_role" className="col-span-3 sm:col-span-3 h-8 rounded-none text-xs" />
                  <Input type="number" value={t.due_offset_days} onChange={(e) => set("task_templates", form.task_templates.map((x, idx) => idx === i ? { ...x, due_offset_days: Number(e.target.value) } : x))} className="col-span-2 sm:col-span-2 h-8 rounded-none text-xs" />
                  <Button size="sm" variant="ghost" className="col-span-1 h-8 rounded-none px-1" onClick={() => set("task_templates", form.task_templates.filter((_, idx) => idx !== i))} data-testid={`kit-task-remove-${i}`}><Trash2 className="w-3 h-3" /></Button>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-1.5 mt-1">
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => set("task_templates", [...form.task_templates, { title: "", due_offset_days: 3 }])} data-testid="kit-add-task-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add task
              </Button>
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => setBulkOpen(true)} data-testid="kit-bulk-add-btn">
                <ListPlus className="w-3.5 h-3.5 mr-1" /> Bulk add
              </Button>
            </div>
          </div>
          <div>
            <Label className="label-eyebrow text-[10px]">Auto-launch SOPs</Label>
            <select multiple value={form.sop_ids} onChange={(e) => set("sop_ids", Array.from(e.target.selectedOptions).map((o) => o.value))} className="w-full min-h-24 border border-border p-1 text-xs">
              {sops.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">Hold Cmd/Ctrl to pick multiple.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="kit-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
      {bulkOpen && (
        <KitBulkAddTasksDialog
          onClose={() => setBulkOpen(false)}
          onAdd={(list) => {
            set("task_templates", [...form.task_templates, ...list]);
            setBulkOpen(false);
          }}
        />
      )}
    </Dialog>
  );
}

// Feb 2026 — Paste multiple task titles (one per line) and pick shared
// owner_role + due_offset_days once. Faster than 20 rounds of clicking
// "Add task" when seeding a new kit.
function KitBulkAddTasksDialog({ onClose, onAdd }) {
  const [rawTitles, setRawTitles] = useState("");
  const [shared, setShared] = useState({ owner_role: "hr", due_offset_days: 3 });
  const lines = rawTitles.split("\n").map((l) => l.trim()).filter(Boolean);
  const submit = () => {
    if (lines.length === 0) return;
    onAdd(lines.map((title) => ({
      title,
      owner_role: shared.owner_role,
      due_offset_days: Number(shared.due_offset_days) || 0,
    })));
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="kit-bulk-add-dialog">
        <DialogHeader>
          <DialogTitle>Bulk add kit tasks</DialogTitle>
          <p className="text-xs text-muted-foreground">One title per line. All tasks share the values below.</p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px]">Titles</Label>
            <textarea
              value={rawTitles}
              onChange={(e) => setRawTitles(e.target.value)}
              placeholder={"Set up laptop\nProvision Slack access\nSchedule 1:1 with manager"}
              rows={7}
              className="w-full border border-border p-2 rounded-none text-xs font-mono resize-none"
              autoFocus
              data-testid="kit-bulk-add-titles"
            />
            <div className="text-[10px] text-muted-foreground mt-1">{lines.length} task{lines.length === 1 ? "" : "s"} detected</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow text-[10px]">Owner role</Label>
              <Input value={shared.owner_role} onChange={(e) => setShared((s) => ({ ...s, owner_role: e.target.value }))} placeholder="hr" className="rounded-none h-8 text-xs" data-testid="kit-bulk-add-owner" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Due offset (days)</Label>
              <Input type="number" value={shared.due_offset_days} onChange={(e) => setShared((s) => ({ ...s, due_offset_days: e.target.value }))} className="rounded-none h-8 text-xs" data-testid="kit-bulk-add-offset" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none"><X className="w-4 h-4 mr-1" /> Cancel</Button>
          <Button onClick={submit} disabled={lines.length === 0} className="rounded-none" data-testid="kit-bulk-add-submit">
            Add {lines.length || ""} task{lines.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KitLauncher({ kit, onClose, onLaunched }) {
  const { activeWorkspaceId } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/workspaces/${activeWorkspaceId}/clients`).then(({ data }) => {
      const list = Array.isArray(data) ? data : (data?.clients || data?.items || []);
      setClients(list);
    });
  }, [activeWorkspaceId]);

  const launch = async () => {
    if (!clientId) { toast.error("Pick a client"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding-kits/${kit.id}/launch`, {
        client_id: clientId, contact_email: email || null, contact_phone: phone || null,
      });
      setResult(data);
      toast.success("Kit launched");
    } catch (e) { toast.error(apiErrorMessage(e, "Launch failed")); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!result?.public_url) return;
    const full = window.location.origin + result.public_url;
    navigator.clipboard.writeText(full).then(() => toast.success("Link copied"));
  };

  return (
    <Dialog open onOpenChange={() => { if (result) onLaunched(); onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="kit-launcher">
        <DialogHeader><DialogTitle>Launch: {kit.name}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-3">
            <div className="text-sm">✅ Kit launched. {result.launch.task_ids.length} tasks · {result.launch.sop_run_ids.length} SOP runs.</div>
            {result.public_url && (
              <div className="border border-border p-3 bg-muted/20">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Public questionnaire link</div>
                <div className="text-xs font-mono truncate" data-testid="kit-launch-url">{window.location.origin + result.public_url}</div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" className="rounded-none" onClick={copyLink} data-testid="kit-copy-url"><CopyIcon className="w-3.5 h-3.5 mr-1" /> Copy</Button>
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => window.open(result.public_url, "_blank")}><ExternalLink className="w-3.5 h-3.5 mr-1" /> Open</Button>
                  {result.launch?.client_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-none"
                      onClick={async () => {
                        try {
                          // Grab an active portal link for the client and open the preview.
                          const { data } = await api.get(`/clients/${result.launch.client_id}/portal-links`);
                          const link = Array.isArray(data) ? data.find((l) => !l.revoked_at) : null;
                          if (!link) { toast.info("Client doesn't have an active portal token — generate one from the client page."); return; }
                          window.open(`/portal/${link.token}?preview=announcement`, "_blank");
                        } catch (e) { toast.error(apiErrorMessage(e, "Couldn't open portal")); }
                      }}
                      data-testid="kit-preview-portal"
                    ><ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview in portal</Button>
                  )}
                </div>
                {/* Iter 126b — QR code for laptop-share moments */}
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/60" data-testid="kit-launch-qr">
                  <QRCodeSVG value={window.location.origin + result.public_url} size={96} includeMargin={false} />
                  <div className="text-[11px] text-muted-foreground flex-1">
                    <div className="font-medium text-foreground flex items-center gap-1 mb-1"><QrCode className="w-3.5 h-3.5" /> Scan to open on phone</div>
                    Show this on-screen — the client scans and completes the intake without needing the email to land.
                  </div>
                </div>
              </div>
            )}
            <div className="text-[11px] text-muted-foreground">
              Delivery: {Object.entries(result.delivery.channels).map(([c, r]) => `${c} ${r.sent ? "✓" : "✗"}`).join(" · ")}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label className="label-eyebrow text-[10px]">Client</Label>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="h-9 rounded-none w-full border border-border bg-background px-2 text-sm" data-testid="kit-launch-client">
                <option value="">— pick a client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div><Label className="label-eyebrow text-[10px]">Contact email (fallback: client.billing_email)</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} className="h-9 rounded-none" placeholder="contact@example.com" data-testid="kit-launch-email" /></div>
            <div><Label className="label-eyebrow text-[10px]">Contact phone (WhatsApp)</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-9 rounded-none" placeholder="+91…" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Close</Button>
          {!result && <Button onClick={launch} disabled={busy || !clientId} className="rounded-none" data-testid="kit-launch-confirm">{busy ? "Launching…" : "Launch kit"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────── Responses ───────────────────────

function ResponsesTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try { const { data } = await api.get(`/workspaces/${activeWorkspaceId}/questionnaire-responses`); setRows(data || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Couldn't load")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const submitted = useMemo(() => rows.filter((r) => r.status === "submitted"), [rows]);
  const pending = useMemo(() => rows.filter((r) => r.status !== "submitted"), [rows]);

  return (
    <div className="space-y-3" data-testid="responses-tab">
      {loading ? <div className="text-sm text-muted-foreground py-6">Loading…</div> : (
        <div className="grid md:grid-cols-2 gap-4">
          <ResponsesList title={`Submitted (${submitted.length})`} rows={submitted} onSelect={setSelected} />
          <ResponsesList title={`Pending (${pending.length})`} rows={pending} onSelect={setSelected} />
        </div>
      )}
      {selected && (
        <Dialog open onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="response-viewer">
            <DialogHeader><DialogTitle>{selected.questionnaire_name}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="text-[11px] text-muted-foreground">Client: {selected.client_id?.slice(0, 8)} · Status: <b>{selected.status}</b> · Launched {selected.launched_at?.slice(0, 16).replace('T', ' ')}</div>
              {selected.ai_summary && (
                <div className="border border-primary/30 bg-primary/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-primary mb-1">AI Executive Summary</div>
                  <p className="text-sm">{selected.ai_summary}</p>
                </div>
              )}
              {selected.ai_flags?.length > 0 && (
                <div className="border border-red-300 bg-red-50 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-red-700 mb-1">🚩 Red flags</div>
                  <ul className="list-disc pl-4 text-sm">{selected.ai_flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
              <div className="border border-border">
                <div className="px-3 py-2 bg-muted/30 border-b text-xs font-semibold uppercase tracking-widest">Answers</div>
                {Object.entries(selected.answers || {}).length === 0 ? <div className="p-3 text-[11px] italic text-muted-foreground">No answers yet.</div>
                : Object.entries(selected.answers || {}).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-4 gap-2 px-3 py-2 border-b border-border last:border-b-0">
                    <div className="col-span-1 text-[11px] font-mono text-muted-foreground">{k}</div>
                    <div className="col-span-3 text-sm whitespace-pre-wrap break-words">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ResponsesList({ title, rows, onSelect }) {
  return (
    <div className="border border-border">
      <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-semibold uppercase tracking-widest">{title}</div>
      {rows.length === 0 ? <div className="p-3 text-[11px] italic text-muted-foreground">Nothing here.</div> :
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.id} className="p-2 text-xs hover:bg-secondary/20 cursor-pointer" onClick={() => onSelect(r)} data-testid={`response-row-${r.id}`}>
              <div className="font-medium truncate">{r.questionnaire_name}</div>
              <div className="text-[10px] text-muted-foreground truncate">Client: {r.client_id?.slice(0, 8)} · {(r.submitted_at || r.launched_at || "").slice(0, 16).replace('T', ' ')}</div>
              {r.ai_flags?.length > 0 && <div className="text-[10px] text-red-600">🚩 {r.ai_flags.length} flag(s)</div>}
            </li>
          ))}
        </ul>}
    </div>
  );
}


// ─────────────── Save-as-template dialog ───────────────

function SaveAsTemplateDialog({ kit, wsId, onClose, onSaved }) {
  const [name, setName] = useState(kit?.name || "");
  const [description, setDescription] = useState(kit?.description || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Give the template a name"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/onboarding-kits/${kit.id}/save-as-template`, {
        name: name.trim(),
        description: description.trim() || null,
      });
      toast.success("Kit saved as workspace template");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none" data-testid="save-as-template-dialog">
        <DialogHeader><DialogTitle>Save kit as workspace template</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Creates a private, workspace-scoped snapshot of <strong>{kit?.name}</strong> that other admins can clone repeatedly from the <em>Template library</em>. The linked questionnaire is snapshotted too, so future clones re-create it.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px]">Template name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-none" data-testid="save-as-template-name" />
          </div>
          <div>
            <Label className="label-eyebrow text-[10px]">Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="rounded-none" data-testid="save-as-template-desc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="save-as-template-save">{busy ? "Saving…" : "Save template"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Iter 131 — Cross-workspace kit template sharing ───────────────

function ShareTemplateDialog({ template, wsId, onClose }) {
  const [ttl, setTtl] = useState(30);
  const [token, setToken] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);

  const mint = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/kit-templates/${template.id}/share`, { ttl_days: Number(ttl) || 30 });
      setToken(data.token);
      setExpires(data.expires_at);
      toast.success("Share link ready — copy it to the destination workspace admin.");
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't create share link")); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success("Copied to clipboard");
    } catch { toast.error("Clipboard blocked — copy the token manually"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none" data-testid="share-template-dialog">
        <DialogHeader><DialogTitle>Share &quot;{template.name}&quot;</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Mint a signed, time-limited token. Any admin/owner of another Acquilix workspace can paste it into <em>Onboarding kits → Import via share link</em> to clone this template (kit + questionnaire) into their workspace. SOP IDs are dropped because they are workspace-scoped.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px]">Token lifetime (days)</Label>
            <Input type="number" min={1} max={180} value={ttl} onChange={(e) => setTtl(e.target.value)} className="h-9 rounded-none w-24" data-testid="share-ttl-input" />
          </div>
          {!token ? (
            <Button onClick={mint} disabled={busy} className="rounded-none w-full" data-testid="share-mint-btn">
              {busy ? "Signing…" : "Generate share token"}
            </Button>
          ) : (
            <>
              <div>
                <Label className="label-eyebrow text-[10px]">Share token (paste in destination workspace)</Label>
                <Textarea value={token} readOnly rows={5} className="rounded-none font-mono text-[10px] break-all" data-testid="share-token-output" />
              </div>
              <div className="text-[11px] text-muted-foreground">Expires: {expires.slice(0, 16).replace("T", " ")}</div>
              <div className="flex gap-2">
                <Button variant="outline" className="rounded-none flex-1" onClick={copy} data-testid="share-copy-btn"><CopyIcon className="w-3.5 h-3.5 mr-1" /> Copy token</Button>
                <Button variant="ghost" className="rounded-none" onClick={() => { setToken(""); setExpires(""); }}>Generate another</Button>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportViaTokenDialog({ wsId, onClose, onImported }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (token.trim().length < 20) { toast.error("Paste the full share token"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/kit-templates/import`, { token: token.trim(), target_workspace_id: wsId });
      toast.success(`Kit "${data.name}" imported successfully`);
      onImported();
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none" data-testid="import-token-dialog">
        <DialogHeader><DialogTitle>Import kit template from another workspace</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Paste a share token generated by an admin in a partner workspace. A new kit will be created in <strong>this</strong> workspace with the shared welcome, tasks, channels and questionnaire snapshot.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px]">Share token</Label>
            <Textarea value={token} onChange={(e) => setToken(e.target.value)} rows={5} className="rounded-none font-mono text-[10px]" placeholder="eyJhbGci…" data-testid="import-token-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none" data-testid="import-token-submit">{busy ? "Importing…" : "Import kit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

